/**
 * CreateTaskFromEmailDialog — Dialog zum Erstellen einer Aufgabe aus einer E-Mail
 * 
 * Features:
 * - Vorausgefüllt mit Betreff, Absender, E-Mail-Text
 * - Anhänge aus der E-Mail werden automatisch übernommen
 * - "E-Mail-Antwort erwartet" Toggle mit Fälligkeitsdatum
 * - "Stellungnahme erforderlich?" Toggle → verknüpft mit Stellungnahmen-Assistent
 * - E-Mail-Typ Erkennung (Stellungnahme, Anwalt, Versicherung, Kunde, etc.)
 * - Betrieb-Zuordnung (Gutachten, Mietwagen, Autoglas, Werkstatt, etc.)
 * - Aktenverknüpfung (Suche nach Aktenzeichen/Kennzeichen)
 * - Priorität, Zuweisen, Checkliste
 * - Deep-Link: Speichert Thread-ID für Navigation zurück zur E-Mail
 * - Mobil-optimiert
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { 
  X, Plus, Trash2, CheckSquare, Mail, Paperclip, 
  Clock, AlertCircle, FileText, Image, File, Send,
  CalendarClock, User, Flag, Building2, Search,
  FileWarning, Scale, Briefcase, Car, Shield,
  Wrench, GlassWater, Receipt, Users, Link2, Loader2,
  Sparkles, ChevronDown, ChevronUp
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { ChecklistItem, EmailAufgabeMetadata, EmailAnhang, EmailTyp } from '@/lib/database.types';
import { serializeEmailMetadata } from '@/lib/database.types';
import { toast } from 'sonner';

const KOOP_RED = '#C41E3A';

// ─── E-Mail-Typ Konfiguration ─────────────────────────────────────────────
const EMAIL_TYP_CONFIG: Record<EmailTyp, { label: string; icon: React.ElementType; color: string; bgColor: string; beschreibung: string }> = {
  stellungnahme:  { label: 'Stellungnahme',    icon: Scale,        color: '#DC2626', bgColor: '#FEF2F2', beschreibung: 'Prüfbericht / Kürzung — Stellungnahme nötig' },
  anwalt_anfrage: { label: 'Anwalt',           icon: Briefcase,    color: '#7C3AED', bgColor: '#F5F3FF', beschreibung: 'Dokumente oder Anfrage vom Anwalt' },
  versicherung:   { label: 'Versicherung',     icon: Shield,       color: '#2563EB', bgColor: '#EFF6FF', beschreibung: 'Korrespondenz mit Versicherung' },
  kunde:          { label: 'Kunde',            icon: Users,        color: '#059669', bgColor: '#F0FDF4', beschreibung: 'Anfrage oder Nachricht vom Kunden' },
  werkstatt:      { label: 'Werkstatt',        icon: Wrench,       color: '#D97706', bgColor: '#FFFBEB', beschreibung: 'Werkstatt-bezogene Korrespondenz' },
  mietwagen:      { label: 'Mietwagen',        icon: Car,          color: '#EA580C', bgColor: '#FFF7ED', beschreibung: 'Mietwagen / Ersatzfahrzeug' },
  autoglas:       { label: 'Autoglas',         icon: GlassWater,   color: '#0891B2', bgColor: '#ECFEFF', beschreibung: 'Autoglas / Lenneglas' },
  rechnung:       { label: 'Rechnung',         icon: Receipt,      color: '#4F46E5', bgColor: '#EEF2FF', beschreibung: 'Rechnungsbezogene E-Mail' },
  allgemein:      { label: 'Allgemein',        icon: Mail,         color: '#6B7280', bgColor: '#F9FAFB', beschreibung: 'Sonstige E-Mail' },
};

// ─── Betrieb-Optionen ─────────────────────────────────────────────────────
const BETRIEB_OPTIONS = [
  { id: 'gutachten',      label: 'Kfz Gutachten',    kuerzel: 'GA', color: '#2563EB' },
  { id: 'autoglas',       label: 'Autoglas',          kuerzel: 'AG', color: '#059669' },
  { id: 'mietwagen',      label: 'Mietwagen',         kuerzel: 'MW', color: '#EA580C' },
  { id: 'werkstatt',      label: 'Werkstatt',         kuerzel: 'WK', color: '#D97706' },
  { id: 'unfallgiganten', label: 'Unfallgiganten',    kuerzel: 'UG', color: '#DC2626' },
  { id: 'protex',         label: 'Protex',            kuerzel: 'PX', color: '#7C3AED' },
  { id: 'recht',          label: 'Recht',             kuerzel: 'RE', color: '#374151' },
];

// E-Mail-Nachricht Typ (kompatibel mit useGmail.GmailMessage)
interface GmailMessage {
  id: string;
  threadId: string;
  subject: string;
  from: string;
  fromName?: string;
  to: string;
  date: string;
  snippet: string;
  body?: string;
  bodyHtml?: string;
  labelIds: string[];
  isUnread?: boolean;
  cc?: string;
  attachments?: Array<{
    filename: string;
    mimeType: string;
    size: number;
    attachmentId: string;
  }>;
}

interface Props {
  open: boolean;
  onClose: () => void;
  email: GmailMessage;
  currentUserId?: string;
  onSuccess?: (aufgabeId: string) => void;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return <Image className="w-4 h-4 text-blue-500" />;
  if (mimeType.includes('pdf')) return <FileText className="w-4 h-4 text-red-500" />;
  return <File className="w-4 h-4 text-gray-500" />;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Intelligente E-Mail-Typ-Erkennung ────────────────────────────────────
function detectEmailTyp(subject: string, from: string, body: string, attachments?: Array<{ filename: string }>): { typ: EmailTyp; stellungnahme: boolean; betrieb: string } {
  const text = `${subject} ${from} ${body}`.toLowerCase();
  const attNames = (attachments || []).map(a => a.filename.toLowerCase()).join(' ');
  
  // Stellungnahme-Erkennung (höchste Priorität)
  const stellungnahmeKeywords = ['prüfbericht', 'kürzung', 'kürzungen', 'stellungnahme', 'klageerw', 'gegenseite', 'stellung zu nehmen', 'stellung nehmen', 'einwände', 'einwänden', 'moniert', 'beanstand'];
  const isStellungnahme = stellungnahmeKeywords.some(kw => text.includes(kw)) || attNames.includes('prüfbericht') || attNames.includes('kürzung') || attNames.includes('klageerwiderung');
  
  if (isStellungnahme) {
    return { typ: 'stellungnahme', stellungnahme: true, betrieb: 'gutachten' };
  }
  
  // Anwalt-Erkennung
  const anwaltKeywords = ['rechtsanw', 'anwalt', 'kanzlei', 'fachanwalt', 'notar', 'recht.de', 'anwälte'];
  if (anwaltKeywords.some(kw => text.includes(kw))) {
    return { typ: 'anwalt_anfrage', stellungnahme: false, betrieb: 'gutachten' };
  }
  
  // Versicherung-Erkennung
  const versicherungKeywords = ['versicherung', 'huk', 'allianz', 'axa', 'zurich', 'ergo', 'generali', 'devk', 'vhv', 'lvm', 'provinzial', 'gothaer', 'regulierung', 'schadenregulierung', 'schadennr', 'schadennummer'];
  if (versicherungKeywords.some(kw => text.includes(kw))) {
    return { typ: 'versicherung', stellungnahme: false, betrieb: 'gutachten' };
  }
  
  // Mietwagen-Erkennung
  const mietwagenKeywords = ['mietwagen', 'mietgo', 'ersatzfahrzeug', 'mietvertrag', 'rentsoft'];
  if (mietwagenKeywords.some(kw => text.includes(kw))) {
    return { typ: 'mietwagen', stellungnahme: false, betrieb: 'mietwagen' };
  }
  
  // Autoglas-Erkennung
  const autoglasKeywords = ['autoglas', 'lenneglas', 'scheibentausch', 'windschutzscheibe', 'steinschlag'];
  if (autoglasKeywords.some(kw => text.includes(kw))) {
    return { typ: 'autoglas', stellungnahme: false, betrieb: 'autoglas' };
  }
  
  // Werkstatt-Erkennung
  const werkstattKeywords = ['werkstatt', 'reparatur', 'kostenvoranschlag', 'kvs'];
  if (werkstattKeywords.some(kw => text.includes(kw))) {
    return { typ: 'werkstatt', stellungnahme: false, betrieb: 'werkstatt' };
  }
  
  // Rechnung-Erkennung
  const rechnungKeywords = ['rechnung', 'invoice', 'zahlung', 'überweisung', 'mahnung', 'getmyinvoice'];
  if (rechnungKeywords.some(kw => text.includes(kw))) {
    return { typ: 'rechnung', stellungnahme: false, betrieb: 'gutachten' };
  }
  
  return { typ: 'allgemein', stellungnahme: false, betrieb: 'gutachten' };
}

// ─── Aktenzeichen aus E-Mail extrahieren ──────────────────────────────────
function extractAktenzeichen(subject: string, body: string): string[] {
  const text = `${subject} ${body}`;
  const patterns = [
    /AK\d{7}/gi,                          // AK2507094
    /\b\d{3}\/\d{2}\b/g,                  // 499/25
    /SN-\d{4}-[A-Z]+-\d+/gi,             // SN-2026-HUK-88432
    /[A-Z]{2,3}-[A-Z]{1,2}\s?\d{1,4}/gi, // MK-AK 1234
  ];
  
  const results: string[] = [];
  for (const pattern of patterns) {
    const matches = text.match(pattern);
    if (matches) results.push(...matches);
  }
  return [...new Set(results)];
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────
export default function CreateTaskFromEmailDialog({ open, onClose, email, currentUserId, onSuccess }: Props) {
  // Form State
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [prioritaet, setPrioritaet] = useState<string>('mittel');
  const [faelligAm, setFaelligAm] = useState('');
  const [antwortErwartet, setAntwortErwartet] = useState(false);
  const [antwortFaelligBis, setAntwortFaelligBis] = useState('');
  const [selectedAnhaenge, setSelectedAnhaenge] = useState<Set<number>>(new Set());
  const [checkliste, setCheckliste] = useState<ChecklistItem[]>([]);
  const [neuerChecklistenPunkt, setNeuerChecklistenPunkt] = useState('');
  const [saving, setSaving] = useState(false);
  
  // NEU: Erweiterte Felder
  const [emailTyp, setEmailTyp] = useState<EmailTyp>('allgemein');
  const [stellungnahmeErforderlich, setStellungnahmeErforderlich] = useState(false);
  const [betrieb, setBetrieb] = useState('gutachten');
  const [aktenSuche, setAktenSuche] = useState('');
  const [aktenResults, setAktenResults] = useState<Array<{ id: string; akten_nummer: string; kennzeichen?: string; kunde_name?: string }>>([]);
  const [selectedAkte, setSelectedAkte] = useState<{ id: string; akten_nummer: string } | null>(null);
  const [aktenLoading, setAktenLoading] = useState(false);
  const [showErweitert, setShowErweitert] = useState(false);

  // Intelligente Erkennung beim Öffnen
  useEffect(() => {
    if (open && email) {
      const detected = detectEmailTyp(
        email.subject || '',
        email.from || '',
        email.body || email.snippet || '',
        email.attachments
      );
      
      setEmailTyp(detected.typ);
      setStellungnahmeErforderlich(detected.stellungnahme);
      setBetrieb(detected.betrieb);
      
      // Priorität basierend auf Typ setzen
      if (detected.typ === 'stellungnahme') {
        setPrioritaet('hoch');
      } else if (detected.typ === 'anwalt_anfrage') {
        setPrioritaet('hoch');
      } else {
        setPrioritaet('mittel');
      }
      
      setTitel(email.subject || 'Aufgabe aus E-Mail');
      const plainText = email.body || email.snippet || '';
      setBeschreibung(
        `Von: ${email.fromName || email.from}\nDatum: ${new Date(email.date).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}\n\n${plainText.substring(0, 2000)}`
      );
      
      const in3Tagen = new Date();
      in3Tagen.setDate(in3Tagen.getDate() + (detected.typ === 'stellungnahme' ? 7 : 3));
      setFaelligAm(in3Tagen.toISOString().split('T')[0]);
      setAntwortErwartet(false);
      setAntwortFaelligBis('');
      
      if (email.attachments && email.attachments.length > 0) {
        setSelectedAnhaenge(new Set(email.attachments.map((_, i) => i)));
      } else {
        setSelectedAnhaenge(new Set());
      }
      setCheckliste([]);
      setNeuerChecklistenPunkt('');
      setSelectedAkte(null);
      setAktenSuche('');
      setAktenResults([]);
      
      // Aktenzeichen aus E-Mail extrahieren und vorausfüllen
      const aktenzeichen = extractAktenzeichen(email.subject || '', email.body || email.snippet || '');
      if (aktenzeichen.length > 0) {
        setAktenSuche(aktenzeichen[0]);
        setShowErweitert(true);
      }
      
      // Bei Stellungnahme automatisch erweitert anzeigen
      if (detected.stellungnahme) {
        setShowErweitert(true);
      }
    }
  }, [open, email]);

  // Akten-Suche
  useEffect(() => {
    if (!aktenSuche || aktenSuche.length < 2) {
      setAktenResults([]);
      return;
    }
    
    const timer = setTimeout(async () => {
      setAktenLoading(true);
      try {
        const { data } = await supabase
          .from('akten')
          .select('id, akten_nummer, kennzeichen, kunde_name')
          .or(`akten_nummer.ilike.%${aktenSuche}%,kennzeichen.ilike.%${aktenSuche}%,kunde_name.ilike.%${aktenSuche}%,schadennummer.ilike.%${aktenSuche}%`)
          .limit(5);
        setAktenResults(data || []);
      } catch {
        setAktenResults([]);
      } finally {
        setAktenLoading(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [aktenSuche]);

  const addChecklistItem = useCallback(() => {
    if (!neuerChecklistenPunkt.trim()) return;
    setCheckliste(prev => [
      ...prev,
      { id: crypto.randomUUID(), text: neuerChecklistenPunkt.trim(), done: false }
    ]);
    setNeuerChecklistenPunkt('');
  }, [neuerChecklistenPunkt]);

  const removeChecklistItem = useCallback((id: string) => {
    setCheckliste(prev => prev.filter(item => item.id !== id));
  }, []);

  const toggleAnhang = useCallback((index: number) => {
    setSelectedAnhaenge(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titel.trim()) {
      toast.error('Bitte Titel eingeben');
      return;
    }

    setSaving(true);
    try {
      const anhaenge: EmailAnhang[] = (email.attachments || [])
        .filter((_, i) => selectedAnhaenge.has(i))
        .map(a => ({
          filename: a.filename,
          mimeType: a.mimeType,
          size: a.size,
          attachmentId: a.attachmentId,
        }));

      const emailMeta: EmailAufgabeMetadata = {
        type: 'email_aufgabe',
        messageId: email.id,
        threadId: email.threadId,
        from: email.from,
        fromName: email.fromName || email.from,
        subject: email.subject,
        date: email.date,
        antwortErwartet,
        antwortFaelligBis: antwortErwartet ? antwortFaelligBis : undefined,
        anhaenge,
        // NEU: Erweiterte Felder
        emailTyp,
        stellungnahmeErforderlich,
        aktenverknuepfung: selectedAkte ? {
          akteId: selectedAkte.id,
          aktenzeichen: selectedAkte.akten_nummer,
        } : undefined,
        betrieb,
      };

      const insertData: Record<string, any> = {
        titel: titel.trim(),
        beschreibung: beschreibung.trim() || null,
        prioritaet,
        kategorie: stellungnahmeErforderlich ? 'stellungnahme' : 'email_aufgabe',
        status: 'offen',
        business_unit: betrieb,
        faellig_am: faelligAm || null,
        wiedervorlage_notiz: serializeEmailMetadata(emailMeta),
      };

      // Aktenverknüpfung
      if (selectedAkte) {
        insertData.akte_id = selectedAkte.id;
      }

      // Wiedervorlage-Datum
      if (antwortErwartet && antwortFaelligBis) {
        insertData.wiedervorlage_datum = antwortFaelligBis;
      }

      // Zuweisen
      if (currentUserId) {
        insertData.erstellt_von = currentUserId;
        insertData.zugewiesen_an = currentUserId;
        insertData.zugewiesen_an_liste = [currentUserId];
      }

      const { data, error } = await supabase
        .from('aufgaben')
        .insert(insertData)
        .select('id')
        .single();

      if (error) throw error;

      // Checkliste
      if (checkliste.length > 0 && data?.id) {
        try {
          await supabase
            .from('aufgaben')
            .update({ checkliste } as any)
            .eq('id', data.id);
        } catch {
          console.warn('Checkliste konnte nicht gespeichert werden');
        }
      }

      // Erfolgs-Toast mit Typ-Info
      const typConfig = EMAIL_TYP_CONFIG[emailTyp];
      toast.success('Aufgabe erstellt', {
        description: `${typConfig.label}: "${titel.trim()}"${selectedAkte ? ` — Akte ${selectedAkte.akten_nummer}` : ''}${stellungnahmeErforderlich ? ' — Stellungnahme erforderlich' : ''}`,
        action: stellungnahmeErforderlich ? {
          label: 'Stellungnahme starten',
          onClick: () => {
            if (selectedAkte) {
              window.location.href = `/akten/${selectedAkte.id}?tab=dokumente&aktion=stellungnahme`;
            } else {
              window.location.href = '/aufgaben';
            }
          },
        } : {
          label: 'Zur Aufgabe',
          onClick: () => window.location.href = '/aufgaben',
        },
      });

      onSuccess?.(data.id);
      onClose();
    } catch (err: any) {
      console.error('Fehler beim Erstellen der Aufgabe:', err);
      toast.error('Fehler', { description: err.message || 'Aufgabe konnte nicht erstellt werden' });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const activeTypConfig = EMAIL_TYP_CONFIG[emailTyp];

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full sm:h-auto rounded-t-2xl sm:rounded-2xl shadow-2xl sm:max-w-lg max-h-[90vh] sm:mx-4 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: `${activeTypConfig.color}20` }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: activeTypConfig.bgColor }}>
              <activeTypConfig.icon className="w-5 h-5" style={{ color: activeTypConfig.color }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Aufgabe aus E-Mail</h2>
              <p className="text-xs text-gray-500">Von {email.fromName || email.from}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-4">
            
            {/* ═══ E-Mail-Typ (KI-erkannt) ═══ */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Sparkles className="w-3.5 h-3.5 inline mr-1 text-purple-500" />
                E-Mail-Typ <span className="text-xs font-normal text-purple-500">(automatisch erkannt)</span>
              </label>
              <div className="grid grid-cols-3 gap-1.5">
                {(Object.entries(EMAIL_TYP_CONFIG) as [EmailTyp, typeof EMAIL_TYP_CONFIG[EmailTyp]][]).map(([typ, cfg]) => {
                  const Icon = cfg.icon;
                  const isActive = emailTyp === typ;
                  return (
                    <button
                      key={typ}
                      type="button"
                      onClick={() => {
                        setEmailTyp(typ);
                        if (typ === 'stellungnahme') {
                          setStellungnahmeErforderlich(true);
                          setBetrieb('gutachten');
                        }
                        // Auto-Betrieb zuordnen
                        if (typ === 'mietwagen') setBetrieb('mietwagen');
                        if (typ === 'autoglas') setBetrieb('autoglas');
                        if (typ === 'werkstatt') setBetrieb('werkstatt');
                      }}
                      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                        isActive 
                          ? 'border-current shadow-sm' 
                          : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                      style={isActive ? { color: cfg.color, backgroundColor: cfg.bgColor, borderColor: cfg.color } : undefined}
                    >
                      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{cfg.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ═══ Stellungnahme erforderlich? ═══ */}
            <div className={`p-3 rounded-xl border transition-all ${
              stellungnahmeErforderlich 
                ? 'border-red-300 bg-red-50/50' 
                : 'border-gray-200 bg-gray-50/30'
            }`}>
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={stellungnahmeErforderlich}
                    onChange={e => setStellungnahmeErforderlich(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-10 h-6 rounded-full transition-colors ${stellungnahmeErforderlich ? 'bg-red-500' : 'bg-gray-300'}`}>
                    <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform mt-1 ${stellungnahmeErforderlich ? 'translate-x-5 ml-0' : 'translate-x-1'}`} />
                  </div>
                </div>
                <div className="flex-1">
                  <span className={`text-sm font-medium ${stellungnahmeErforderlich ? 'text-red-800' : 'text-gray-900'}`}>
                    Stellungnahme erforderlich?
                  </span>
                  <p className="text-xs text-gray-500">
                    {stellungnahmeErforderlich 
                      ? 'Wird im Stellungnahmen-Assistenten vorbereitet' 
                      : 'Prüfbericht / Kürzung erfordert fachliche Stellungnahme'}
                  </p>
                </div>
                {stellungnahmeErforderlich && (
                  <Scale className="w-5 h-5 text-red-500 flex-shrink-0" />
                )}
              </label>
            </div>

            {/* ═══ Betrieb & Priorität ═══ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Building2 className="w-3.5 h-3.5 inline mr-1" />
                  Betrieb
                </label>
                <select
                  value={betrieb}
                  onChange={e => setBetrieb(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  {BETRIEB_OPTIONS.map(b => (
                    <option key={b.id} value={b.id}>{b.kuerzel} — {b.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Flag className="w-3.5 h-3.5 inline mr-1" />
                  Priorität
                </label>
                <select
                  value={prioritaet}
                  onChange={e => setPrioritaet(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="niedrig">Niedrig</option>
                  <option value="mittel">Mittel</option>
                  <option value="hoch">Hoch</option>
                  <option value="dringend">Dringend</option>
                </select>
              </div>
            </div>

            {/* ═══ Titel ═══ */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
              <input
                type="text"
                value={titel}
                onChange={e => setTitel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                placeholder="Aufgabentitel..."
                required
              />
            </div>

            {/* ═══ Fällig am ═══ */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <CalendarClock className="w-3.5 h-3.5 inline mr-1" />
                Fällig am
              </label>
              <input
                type="date"
                value={faelligAm}
                onChange={e => setFaelligAm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>

            {/* ═══ Aktenverknüpfung ═══ */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Link2 className="w-3.5 h-3.5 inline mr-1" />
                Aktenverknüpfung
              </label>
              {selectedAkte ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-blue-800 flex-1">{selectedAkte.akten_nummer}</span>
                  <button
                    type="button"
                    onClick={() => { setSelectedAkte(null); setAktenSuche(''); }}
                    className="p-1 rounded hover:bg-blue-100 text-blue-400 hover:text-blue-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={aktenSuche}
                    onChange={e => setAktenSuche(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Aktenzeichen, Kennzeichen oder Name suchen..."
                  />
                  {aktenLoading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                  )}
                  {aktenResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      {aktenResults.map(akte => (
                        <button
                          key={akte.id}
                          type="button"
                          onClick={() => {
                            setSelectedAkte({ id: akte.id, akten_nummer: akte.akten_nummer });
                            setAktenSuche('');
                            setAktenResults([]);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b border-gray-100 last:border-0"
                        >
                          <span className="font-medium text-gray-900">{akte.akten_nummer}</span>
                          {akte.kennzeichen && <span className="text-gray-500 ml-2">{akte.kennzeichen}</span>}
                          {akte.kunde_name && <span className="text-gray-400 ml-2">— {akte.kunde_name}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ═══ E-Mail-Antwort erwartet ═══ */}
            <div className="p-3 rounded-xl border border-amber-200 bg-amber-50/50">
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={antwortErwartet}
                    onChange={e => setAntwortErwartet(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-10 h-6 rounded-full transition-colors ${antwortErwartet ? 'bg-amber-500' : 'bg-gray-300'}`}>
                    <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform mt-1 ${antwortErwartet ? 'translate-x-5 ml-0' : 'translate-x-1'}`} />
                  </div>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-900">E-Mail-Antwort erwartet</span>
                  <p className="text-xs text-gray-500">Erinnerung wenn Antwort fällig</p>
                </div>
              </label>
              
              {antwortErwartet && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-amber-700 mb-1">Antwort fällig bis</label>
                  <input
                    type="date"
                    value={antwortFaelligBis}
                    onChange={e => setAntwortFaelligBis(e.target.value)}
                    className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white"
                  />
                </div>
              )}
            </div>

            {/* ═══ Anhänge aus E-Mail ═══ */}
            {email.attachments && email.attachments.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Paperclip className="w-3.5 h-3.5 inline mr-1" />
                  Anhänge aus E-Mail ({email.attachments.length})
                </label>
                <div className="space-y-1.5">
                  {email.attachments.map((att, i) => (
                    <label
                      key={i}
                      className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                        selectedAnhaenge.has(i) 
                          ? 'border-blue-300 bg-blue-50/50' 
                          : 'border-gray-200 bg-gray-50/50 opacity-60'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAnhaenge.has(i)}
                        onChange={() => toggleAnhang(i)}
                        className="rounded border-gray-300"
                      />
                      {getFileIcon(att.mimeType)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{att.filename}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(att.size)}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* ═══ Erweitert (Beschreibung + Checkliste) ═══ */}
            <button
              type="button"
              onClick={() => setShowErweitert(!showErweitert)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <span className="font-medium">Erweitert</span>
              {showErweitert ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showErweitert && (
              <>
                {/* Beschreibung */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
                  <textarea
                    value={beschreibung}
                    onChange={e => setBeschreibung(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                    placeholder="Aufgabenbeschreibung..."
                  />
                </div>

                {/* Checkliste */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <CheckSquare className="w-3.5 h-3.5 inline mr-1" />
                    Checkliste
                  </label>
                  {checkliste.length > 0 && (
                    <div className="space-y-1.5 mb-2">
                      {checkliste.map(item => (
                        <div key={item.id} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
                          <CheckSquare className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-700 flex-1">{item.text}</span>
                          <button
                            type="button"
                            onClick={() => removeChecklistItem(item.id)}
                            className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={neuerChecklistenPunkt}
                      onChange={e => setNeuerChecklistenPunkt(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addChecklistItem())}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Neuer Punkt..."
                    />
                    <button
                      type="button"
                      onClick={addChecklistItem}
                      className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                    >
                      <Plus className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50/50 flex items-center justify-between gap-3 mobile-dialog-actions sm:pb-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={saving || !titel.trim()}
              className="px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: stellungnahmeErforderlich ? '#DC2626' : KOOP_RED }}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Erstelle...
                </>
              ) : stellungnahmeErforderlich ? (
                <>
                  <Scale className="w-4 h-4" />
                  Stellungnahme-Aufgabe
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Aufgabe erstellen
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
